import ArticleInterface from "@app/Interface/ArticleInterface";
import EmbeddedFileInterface from "@app/Interface/EmbeddedFileInterface";
import ArticleModel from "@app/Model/ArticleModel";
import axios from "@app/Security/AxiosAuthentication";
import htmlParser from "@app/utils/htmlParser";
import ArticleCategoryInterface from "@app/Interface/ArticleCategoryInterface";
import ApiEndpoints from "@app/utils/ApiEndpoints";
import RollbarHelper from "@app/utils/RollbarHelper";
import ErrorResponseInterface from "@app/Interface/ErrorResponseInterface";

export default class ArticleHelper {

    static getArticles = async (controller: AbortController, fpuId: number, pageId: number, itemsPerPage: number | null = null): Promise<ArticleInterface[]> => {

        const baseUrl: string = process.env.API_BASEURL ?? "";

        let endPoint: string = ApiEndpoints.ARTICLE_COLLECTION + `?page=1&fpu_id=${fpuId}&page_id=${pageId}`;

        if (typeof itemsPerPage === "number" && itemsPerPage > 0) {
            endPoint = endPoint + `&itemsPerPage=${itemsPerPage}`;
        } 
        
        const url = baseUrl + endPoint;

        return await axios.get(url, {
            withCredentials: true,
            signal: controller.signal
        })
            .then(response => {

                const dataItems: ArticleInterface[] = response.data["hydra:member"];

                const articles = ArticleHelper.getArticleItemsFromResponseData(dataItems);

                return articles;

            })
            .catch(error => {

                RollbarHelper.logRequestError(error, "getArticles()");

                // return an empty set of navItems
                const emptyItems: Array<ArticleInterface> = [];

                return emptyItems;

            });

    }

    /**
     * gets the an array of articles from response data
     * 
     * @param dataItems A list of articles 
     * @returns 
     */
    static getArticleItemsFromResponseData = (dataItems: ArticleInterface[] | ErrorResponseInterface): ArticleInterface[] => {

        const articles: ArticleInterface[] = [];

        if (Array.isArray(dataItems)) {

            dataItems.forEach((dataItem) => {

                const article = new ArticleModel();
    
                article.id = dataItem.id;
                article.slug = dataItem.slug;
                article.title = htmlParser.htmlDecode(dataItem.title);
                article.excerpt =dataItem.excerpt;
                article.date = new Date(dataItem.date);
                article.body = dataItem.body;
                article.categories = ArticleHelper.getArticleCategoriesFromResponseData(dataItem.categories);
    
                // data from middleware is a string and needs to be converted to an object
                if (typeof dataItem.featuredImage === "string") {
                    article.featuredImage = ArticleHelper.getFeaturedImage(dataItem.featuredImage);
                }
    
                articles.push(article);
    
            });

        }

        return articles;

    }

    static getFeaturedImage = (embeededFileData: string): EmbeddedFileInterface => {

        // convert to json object. 
        // NOTE: through the magic of interfaces we can strongly type the parsed data
        const featuredImage: EmbeddedFileInterface = JSON.parse(embeededFileData);

        return featuredImage;

    }

    /**
     * Filters the articles based on category
     * @param articles 
     * @param category 
     * @returns 
     */
    static filterArticlesByCategory = (articles: ArticleInterface[], category: string): ArticleInterface[] => {

        const filteredArticles: ArticleInterface[] = [];

        articles.forEach(article => {

            const result = article.categories.filter(categoryItem => categoryItem.slug === category);
            
            if (result.length > 0) {
                const tmpArticle = {...article};
                filteredArticles.push(tmpArticle);
            }

        });

        return filteredArticles;

    }

    /**
     * get an article by article's slug
     * @param articles 
     * @param slug 
     * @returns 
     */
    static getArticleBySlug = (articles: ArticleInterface[], slug: string): ArticleInterface|null => {

        return articles.find(article => article.slug === slug) ?? null;

    }

    /**
     * Determines if an article is new
     * see: https://www.w3resource.com/javascript-exercises/javascript-date-exercise-47.php
     * 
     * @param article 
     * @param weekThreshold 
     * @returns 
     */
     static isNewArticle = (article: ArticleInterface, weekThreshold: number): boolean => {

        let isNew = false;

        const now = new Date();

        const diff = (now.getTime() - article.date.getTime()) / 1000;

        const weeks = diff / (60 * 60 * 24 * 7);

        if (weeks <= weekThreshold) {

            isNew = true;
        }

        return isNew;

    }

    /**
     * Get an a list of WordPress article categories from middleware
     * @param controller AbortController - handles if component unmounts or if cancel button is added
     * @returns 
     */
    static getArticleCategories = async (controller: AbortController): Promise<ArticleCategoryInterface[]> => {

        const baseUrl: string = process.env.API_BASEURL ?? "";

        const url = baseUrl + ApiEndpoints.ARTICLE_CATEGORY_COLLECTION;

        return await axios.get(url, {
            withCredentials: true,
            signal: controller.signal
        })
            .then(response => {

                const dataItems: ArticleCategoryInterface[] = response.data["hydra:member"];

                const categories = ArticleHelper.getArticleCategoriesFromResponseData(dataItems);

                return categories;

            })
            .catch(error => {

                RollbarHelper.logRequestError(error, "getArticleCategories()");

                // return an empty set of navItems
                const emptyItems: Array<ArticleCategoryInterface> = [];

                return emptyItems;

            });

    }

    /**
     * Converts json dataitems into an array of categories
     * @param dataItems 
     * @returns 
     */
    static getArticleCategoriesFromResponseData = (dataItems: ArticleCategoryInterface[]): ArticleCategoryInterface[] => {

        const categories: ArticleCategoryInterface[] = [];

        // decode the name field
        dataItems.map(category => {

            const categoryObject: ArticleCategoryInterface = {
                id: category.id,
                name: htmlParser.htmlDecode(category.name),
                slug: category.slug
            };

            categories.push(categoryObject);

        });

        return categories;

    }

}